// src/components/projects/ProjectForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects';
import { useAuth } from '../../context/AuthContext';
import { projectApi } from '../../api/projectApi';
import useAnalytics from '../../hooks/useAnalytics';
import { GPSButton } from './ProjectMap';
import LoadingSpinner from '../common/LoadingSpinner';
import ProjectPlanner3D from './ProjectPlanner3D';
import {
    ArrowLeftIcon, CheckIcon, CubeIcon, SparklesIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const CURRENCIES = ['GNF', 'EUR', 'GBP', 'XOF', 'NGN', 'GHS', 'XAF'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT', 'CRITICAL'];
const PRIORITY_LABELS = { LOW: 'Bassa', MEDIUM: 'Media', HIGH: 'Alta', URGENT: 'Urgente', CRITICAL: 'Critica' };

const Field = ({ label, required, error, children, hint }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {label} {required && <span className="text-red-400">*</span>}
        </label>
        {children}
        {hint && !error && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
);

const ic = (err) =>
    `w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-gray-50 ${err ? 'border-red-400 bg-red-50' : 'border-gray-200'}`;

const ProjectForm = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const { organization } = useAuth();
    const { createProject, updateProject } = useProjects();
    const { trackProjectCreated, trackEvent, Events } = useAnalytics();

    const [loading, setLoading] = useState(false);
    const [loadingProject, setLoadingProject] = useState(isEdit);
    const [errors, setErrors] = useState({});
    const [show3DPlanner, setShow3DPlanner] = useState(false);
    const [appliedPlan, setAppliedPlan] = useState(null);

    const [form, setForm] = useState({
        code: '',
        name: '',
        description: '',
        location: '',
        latitude: '',
        longitude: '',
        estimatedBudget: '',
        contractAmount: '',
        currency: organization?.currency || 'GNF',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        priority: 'MEDIUM',
        notes: '',
    });

    useEffect(() => { if (isEdit) loadProject(); }, [id]);

    useEffect(() => {
        if (!isEdit && form.name && !form.code) {
            const code = 'PRJ-' + form.name.replace(/\s+/g, '').toUpperCase().slice(0, 6) + '-' + Date.now().toString().slice(-4);
            setForm(prev => ({ ...prev, code }));
        }
    }, [form.name]);

    const loadProject = async () => {
        try {
            setLoadingProject(true);
            const response = await projectApi.getById(id);
            const p = response.data;
            setForm({
                code: p.code || '',
                name: p.name || '',
                description: p.description || '',
                location: p.location || '',
                latitude: p.latitude?.toString() || '',
                longitude: p.longitude?.toString() || '',
                estimatedBudget: p.estimatedBudget?.toString() || '',
                contractAmount: p.contractAmount?.toString() || '',
                currency: p.currency || 'GNF',
                startDate: p.startDate?.split('T')[0] || '',
                endDate: p.endDate?.split('T')[0] || '',
                priority: p.priority || 'MEDIUM',
                notes: p.notes || '',
            });
        } catch {
            toast.error('Errore nel caricamento del progetto');
            navigate('/projects');
        } finally {
            setLoadingProject(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const e = {};
        if (!form.code.trim()) e.code = 'Codice richiesto';
        if (!form.name.trim()) e.name = 'Nome richiesto';
        if (!form.estimatedBudget || parseFloat(form.estimatedBudget) <= 0) e.estimatedBudget = 'Budget deve essere > 0';
        if (!form.startDate) e.startDate = 'Data inizio richiesta';
        if (form.endDate && form.endDate < form.startDate) e.endDate = 'Data fine deve essere dopo la data inizio';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);

        const data = {
            ...form,
            estimatedBudget: parseFloat(form.estimatedBudget),
            contractAmount: form.contractAmount ? parseFloat(form.contractAmount) : undefined,
            endDate: form.endDate || null,
            latitude: form.latitude ? parseFloat(form.latitude) : null,
            longitude: form.longitude ? parseFloat(form.longitude) : null,
            metadata: appliedPlan ? { plan3D: appliedPlan } : undefined,
        };

        const result = isEdit ? await updateProject(id, data) : await createProject(data);
        setLoading(false);

        if (result.success) {
            try {
                if (!isEdit) trackProjectCreated({ currency: form.currency, has_budget: !!form.estimatedBudget, has_gps: !!(form.latitude && form.longitude) });
                else trackEvent(Events.PROJECT_EDITED);
            } catch { }
            navigate(isEdit ? `/projects/${id}` : '/projects');
        } else {
            setErrors({ general: result.error });
        }
    };

    const handlePlanGenerated = (plan) => {
        setAppliedPlan(plan);
        try { trackEvent(Events.AI_PLAN_APPLIED, { structureType: plan.structureType }); } catch { }
        toast.success('Piano 3D applicato al progetto!');
    };

    if (loadingProject) return <LoadingSpinner />;

    return (
        <div className="max-w-3xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex items-center gap-3">
                <button onClick={() => navigate('/projects')}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition">
                    <ArrowLeftIcon className="w-5 h-5" />
                </button>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isEdit ? 'Modifica Progetto' : 'Nuovo Progetto'}
                    </h1>
                    <p className="text-gray-500 text-sm mt-0.5">
                        {isEdit ? 'Aggiorna i dati del progetto' : 'Crea un nuovo progetto di costruzione'}
                    </p>
                </div>
                {!isEdit && (
                    <button onClick={() => setShow3DPlanner(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:opacity-90 transition font-medium text-sm">
                        <CubeIcon className="w-4 h-4" /> Piano 3D + AI
                    </button>
                )}
            </div>

            {appliedPlan && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-2xl p-4 flex items-start gap-3">
                    <SparklesIcon className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-purple-900">Piano 3D AI applicato ✅</p>
                        <p className="text-xs text-purple-600 mt-0.5">
                            Struttura: {appliedPlan.structureType} · {Object.keys(appliedPlan.breakdown || {}).length} categorie
                        </p>
                    </div>
                    <button onClick={() => setAppliedPlan(null)} className="text-purple-400 hover:text-purple-600 text-xs">Rimuovi</button>
                </div>
            )}

            {errors.general && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">⚠️ {errors.general}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>

                {/* Informazioni base */}
                <div className="bg-white rounded-2xl border p-6 space-y-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Informazioni Base</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Nome Progetto" required error={errors.name}>
                            <input type="text" name="name" value={form.name} onChange={handleChange}
                                className={ic(errors.name)} placeholder="Es. Strada N'zérékoré-Lola" />
                        </Field>
                        <Field label="Codice Progetto" required error={errors.code} hint="Auto-generato dal nome">
                            <input type="text" name="code" value={form.code} onChange={handleChange}
                                className={ic(errors.code)} placeholder="Es. PRJ-STRADA-2025" />
                        </Field>
                    </div>
                    <Field label="Descrizione">
                        <textarea name="description" value={form.description} onChange={handleChange}
                            rows={3} className={ic(false)} placeholder="Descrizione del progetto..." />
                    </Field>
                    <Field label="Località">
                        <input type="text" name="location" value={form.location} onChange={handleChange}
                            className={ic(false)} placeholder="Es. Conakry, Guinea" />
                    </Field>

                    {/* ✅ GEOLOCALIZZAZIONE */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3">
                        <div className="flex items-center gap-2">
                            <span className="text-base">📍</span>
                            <p className="text-sm font-semibold text-gray-700">Coordinate GPS</p>
                            {form.latitude && form.longitude && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                                    ✅ Geolocalizzato
                                </span>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Field label="Latitudine" hint="Es. 9.5370">
                                <input type="number" name="latitude" value={form.latitude}
                                    onChange={handleChange} step="0.000001"
                                    className={ic(false)} placeholder="9.5370" />
                            </Field>
                            <Field label="Longitudine" hint="Es. -13.6773">
                                <input type="number" name="longitude" value={form.longitude}
                                    onChange={handleChange} step="0.000001"
                                    className={ic(false)} placeholder="-13.6773" />
                            </Field>
                            <Field label="Rileva automaticamente">
                                <GPSButton
                                    onLocationDetected={({ latitude, longitude }) => {
                                        setForm(prev => ({
                                            ...prev,
                                            latitude: latitude.toFixed(6),
                                            longitude: longitude.toFixed(6),
                                        }));
                                        toast.success(`📍 GPS: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
                                    }}
                                />
                            </Field>
                        </div>
                        {form.latitude && form.longitude && (
                            <p className="text-xs text-gray-400">
                                📌 {parseFloat(form.latitude).toFixed(4)}°N, {parseFloat(form.longitude).toFixed(4)}°E
                                {' · '}
                                <a href={`https://maps.google.com/?q=${form.latitude},${form.longitude}`}
                                    target="_blank" rel="noreferrer"
                                    className="text-blue-500 hover:underline">
                                    Vedi su Google Maps →
                                </a>
                            </p>
                        )}
                    </div>
                </div>

                {/* Budget */}
                <div className="bg-white rounded-2xl border p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">💰 Budget e Contratto</h2>
                        {!isEdit && (
                            <button type="button" onClick={() => setShow3DPlanner(true)}
                                className="ml-auto flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium">
                                <SparklesIcon className="w-3.5 h-3.5" /> Stima con AI
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Valuta">
                            <select name="currency" value={form.currency} onChange={handleChange} className={ic(false)}>
                                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Budget Stimato" required error={errors.estimatedBudget}>
                            <input type="number" name="estimatedBudget" value={form.estimatedBudget} onChange={handleChange}
                                min="0" step="1000" className={ic(errors.estimatedBudget)} placeholder="0" />
                        </Field>
                        <Field label="Importo Contratto" hint="Opzionale">
                            <input type="number" name="contractAmount" value={form.contractAmount} onChange={handleChange}
                                min="0" step="1000" className={ic(false)} placeholder="0" />
                        </Field>
                    </div>
                </div>

                {/* Date */}
                <div className="bg-white rounded-2xl border p-6 space-y-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">📅 Pianificazione</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Data Inizio" required error={errors.startDate}>
                            <input type="date" name="startDate" value={form.startDate} onChange={handleChange}
                                className={ic(errors.startDate)} />
                        </Field>
                        <Field label="Data Fine" hint="Opzionale" error={errors.endDate}>
                            <input type="date" name="endDate" value={form.endDate} onChange={handleChange}
                                className={ic(errors.endDate)} />
                        </Field>
                        <Field label="Priorità">
                            <select name="priority" value={form.priority} onChange={handleChange} className={ic(false)}>
                                {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
                            </select>
                        </Field>
                    </div>
                </div>

                {/* Note */}
                <div className="bg-white rounded-2xl border p-6">
                    <Field label="Note aggiuntive">
                        <textarea name="notes" value={form.notes} onChange={handleChange}
                            rows={2} className={ic(false)} placeholder="Note interne, riferimenti, ecc..." />
                    </Field>
                </div>

                {/* Azioni */}
                <div className="flex gap-3 justify-end pb-6">
                    <button type="button" onClick={() => navigate('/projects')}
                        className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium">
                        Annulla
                    </button>
                    <button type="submit" disabled={loading}
                        className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium disabled:opacity-50">
                        {loading
                            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Salvataggio...</>
                            : <><CheckIcon className="w-4 h-4" />{isEdit ? 'Aggiorna Progetto' : 'Crea Progetto'}</>
                        }
                    </button>
                </div>
            </form>

            {show3DPlanner && (
                <ProjectPlanner3D
                    budget={form.estimatedBudget}
                    currency={form.currency}
                    onPlanGenerated={handlePlanGenerated}
                    onClose={() => setShow3DPlanner(false)}
                />
            )}
        </div>
    );
};

export default ProjectForm;